import { brandStorage as localStorage } from '@/utils/brandSession';
import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import Layout from './Layout';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { 
  Search, Download, RefreshCw, ChevronUp, ChevronDown, Filter,
  User, Calendar, Home, Phone, Mail, FileText, MapPin, X, Eye
} from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../utils/config';

export default function GuestDocumentsTable() {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('created_at');
  const [sortDirection, setSortDirection] = useState('desc');
  const [filters, setFilters] = useState({
    property: 'all',
    citizenship: 'all',
    property_region: 'all',
    property_province: 'all',
    property_city: 'all',
    document_type: 'all',
    status: 'all'
  });
  const [showFilters, setShowFilters] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);

  const [filterOptions, setFilterOptions] = useState({
    properties: [],
    citizenships: [],
    regions: [],
    provinces: [],
    cities: [],
    document_types: []
  });

  useEffect(() => {
    fetchDocuments();
  }, []);

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('hw_token');
      const response = await axios.get(`${API}/api/guest-documents/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const docs = response.data || [];
      setDocuments(docs);
      
      setFilterOptions({
        properties: [...new Set(docs.map(d => d.property_name).filter(Boolean))].sort(),
        citizenships: [...new Set(docs.map(d => d.citizenship).filter(Boolean))].sort(),
        regions: [...new Set(docs.map(d => d.property_region).filter(Boolean))].sort(),
        provinces: [...new Set(docs.map(d => d.property_province).filter(Boolean))].sort(),
        cities: [...new Set(docs.map(d => d.property_city).filter(Boolean))].sort(),
        document_types: [...new Set(docs.map(d => d.document_type).filter(Boolean))].sort()
      });
    } catch (error) {
      console.error('Error fetching documents:', error);
      toast.error('Errore nel caricamento dei documenti');
    } finally {
      setLoading(false);
    }
  };

  const filteredAndSortedDocuments = useMemo(() => {
    let result = [...documents];
    
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      result = result.filter(doc => 
        doc.first_name?.toLowerCase().includes(search) ||
        doc.last_name?.toLowerCase().includes(search) ||
        doc.guest_email?.toLowerCase().includes(search) ||
        doc.guest_phone?.toLowerCase().includes(search) ||
        doc.codice_fiscale?.toLowerCase().includes(search) ||
        doc.document_number?.toLowerCase().includes(search) ||
        doc.property_name?.toLowerCase().includes(search) ||
        doc.citizenship?.toLowerCase().includes(search) ||
        doc.property_city?.toLowerCase().includes(search)
      );
    }
    
    if (filters.property !== 'all') result = result.filter(doc => doc.property_name === filters.property);
    if (filters.citizenship !== 'all') result = result.filter(doc => doc.citizenship === filters.citizenship);
    if (filters.property_region !== 'all') result = result.filter(doc => doc.property_region === filters.property_region);
    if (filters.property_province !== 'all') result = result.filter(doc => doc.property_province === filters.property_province);
    if (filters.property_city !== 'all') result = result.filter(doc => doc.property_city === filters.property_city);
    if (filters.document_type !== 'all') result = result.filter(doc => doc.document_type === filters.document_type);
    if (filters.status !== 'all') result = result.filter(doc => doc.status === filters.status);
    
    result.sort((a, b) => {
      let aVal = a[sortField] || '';
      let bVal = b[sortField] || '';
      if (typeof aVal === 'string') aVal = aVal.toLowerCase();
      if (typeof bVal === 'string') bVal = bVal.toLowerCase();
      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
    
    return result;
  }, [documents, searchTerm, filters, sortField, sortDirection]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <ChevronUp className="h-3 w-3 text-gray-300" />;
    return sortDirection === 'asc' 
      ? <ChevronUp className="h-3 w-3 text-blue-600" />
      : <ChevronDown className="h-3 w-3 text-blue-600" />;
  };

  const clearFilters = () => {
    setFilters({
      property: 'all', citizenship: 'all', property_region: 'all',
      property_province: 'all', property_city: 'all', document_type: 'all', status: 'all'
    });
    setSearchTerm('');
  };

  const exportFullCSV = () => {
    const headers = [
      'Nome', 'Cognome', 'Sesso', 'Tipo Alloggiato', 'Data Nascita', 'Comune Nascita', 'Provincia Nascita', 'Stato Nascita',
      'Cittadinanza', 'Codice Fiscale', 'Indirizzo', 'Comune Residenza', 'Provincia', 'CAP', 'Stato Residenza',
      'Tipo Documento', 'Numero Documento', 'Rilasciato Da', 'Luogo Rilascio', 'Stato Rilascio',
      'Data Rilascio', 'Scadenza', 'Immobile', 'Comune Immobile', 'Provincia Immobile', 'Regione Immobile',
      'Responsabile', 'Check-in', 'Check-out', 'Email', 'Telefono', 'Data Inserimento'
    ];
    
    const rows = filteredAndSortedDocuments.map(doc => [
      doc.first_name, doc.last_name, doc.gender === 'male' ? 'M' : 'F', doc.guest_type || '',
      doc.birth_date, doc.birth_place, doc.birth_province || '', doc.birth_country,
      doc.citizenship, doc.codice_fiscale || '',
      doc.residence_address || '', doc.residence_city, doc.residence_province || '', doc.residence_postal_code || '', doc.residence_country,
      doc.document_type, doc.document_number, doc.document_issuer || '', doc.document_issue_place || '', doc.document_issue_country,
      doc.document_issue_date || '', doc.document_expiry_date || '',
      doc.property_name || '', doc.property_city || '', doc.property_province || '', doc.property_region || '',
      doc.responsible_name || '',
      doc.check_in_date || '', doc.check_out_date || '',
      doc.guest_email || '', doc.guest_phone || '',
      doc.created_at ? new Date(doc.created_at).toLocaleString('it-IT') : ''
    ]);
    
    const csv = [headers.join(';'), ...rows.map(r => r.map(c => `"${c || ''}"`).join(';'))].join('\n');
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `ospiti_completo_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    toast.success(`Esportati ${filteredAndSortedDocuments.length} record completi`);
  };

  const exportMarketingCSV = () => {
    const headers = ['Nome', 'Cognome', 'Email', 'Telefono'];
    const seen = new Set();
    const rows = [];
    
    filteredAndSortedDocuments.forEach(doc => {
      const email = doc.guest_email || '';
      const phone = doc.guest_phone || '';
      if (!email && !phone) return;
      const key = `${(doc.first_name || '').toLowerCase()}_${(doc.last_name || '').toLowerCase()}_${email.toLowerCase()}`;
      if (seen.has(key)) return;
      seen.add(key);
      rows.push([doc.first_name || '', doc.last_name || '', email, phone]);
    });
    
    const csv = [headers.join(';'), ...rows.map(r => r.map(c => `"${c}"`).join(';'))].join('\n');
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `contatti_marketing_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    toast.success(`Esportati ${rows.length} contatti unici per marketing`);
  };

  const activeFiltersCount = Object.values(filters).filter(v => v !== 'all').length + (searchTerm ? 1 : 0);

  const updateFilter = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  return (
    <Layout>
      <div className="space-y-4" data-testid="guest-documents-table">
      <Card>
        <CardHeader className="pb-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Anagrafica Ospiti
              <Badge variant="secondary">{filteredAndSortedDocuments.length} ospiti</Badge>
            </CardTitle>
            <div className="flex items-center gap-2 flex-wrap">
              <Button variant="outline" size="sm" onClick={() => setShowFilters(!showFilters)}
                className={activeFiltersCount > 0 ? 'border-blue-500 text-blue-600' : ''}
                data-testid="toggle-filters-btn">
                <Filter className="h-4 w-4 mr-1" />
                Filtri {activeFiltersCount > 0 && `(${activeFiltersCount})`}
              </Button>
              <Button variant="outline" size="sm" onClick={fetchDocuments} disabled={loading} data-testid="refresh-btn">
                <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
                Aggiorna
              </Button>
              <Button size="sm" variant="outline" onClick={exportFullCSV} disabled={filteredAndSortedDocuments.length === 0}
                data-testid="export-full-btn">
                <Download className="h-4 w-4 mr-1" />
                Export Completo
              </Button>
              <Button size="sm" onClick={exportMarketingCSV} disabled={filteredAndSortedDocuments.length === 0}
                className="bg-green-600 hover:bg-green-700 text-white"
                data-testid="export-marketing-btn">
                <Mail className="h-4 w-4 mr-1" />
                Export Marketing
              </Button>
            </div>
          </div>
          
          <div className="relative mt-4">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input placeholder="Cerca per nome, cognome, email, telefono, CF, immobile, cittadinanza, citta..."
              value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10" data-testid="search-input" />
            {searchTerm && (
              <button onClick={() => setSearchTerm('')}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600">
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          
          {showFilters && (
            <div className="mt-4 p-4 bg-gray-50 rounded-lg space-y-3" data-testid="filters-panel">
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                <FilterSelect label="Immobile" value={filters.property} onChange={v => updateFilter('property', v)}
                  options={filterOptions.properties} testId="filter-property" />
                <FilterSelect label="Cittadinanza (Paese)" value={filters.citizenship} onChange={v => updateFilter('citizenship', v)}
                  options={filterOptions.citizenships} testId="filter-citizenship" />
                <FilterSelect label="Regione Immobile" value={filters.property_region} onChange={v => updateFilter('property_region', v)}
                  options={filterOptions.regions} testId="filter-region" />
                <FilterSelect label="Provincia Immobile" value={filters.property_province} onChange={v => updateFilter('property_province', v)}
                  options={filterOptions.provinces} testId="filter-province" />
                <FilterSelect label="Comune Immobile" value={filters.property_city} onChange={v => updateFilter('property_city', v)}
                  options={filterOptions.cities} testId="filter-city" />
                <FilterSelect label="Tipo Documento" value={filters.document_type} onChange={v => updateFilter('document_type', v)}
                  options={filterOptions.document_types} testId="filter-doctype" />
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Stato</label>
                  <Select value={filters.status} onValueChange={v => updateFilter('status', v)}>
                    <SelectTrigger className="h-9" data-testid="filter-status">
                      <SelectValue placeholder="Tutti" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tutti</SelectItem>
                      <SelectItem value="submitted">Completato</SelectItem>
                      <SelectItem value="pending">In attesa</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {activeFiltersCount > 0 && (
                <Button variant="ghost" size="sm" onClick={clearFilters} className="text-red-600" data-testid="clear-filters-btn">
                  <X className="h-4 w-4 mr-1" />
                  Rimuovi tutti i filtri ({activeFiltersCount})
                </Button>
              )}
            </div>
          )}
        </CardHeader>
        
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="h-8 w-8 animate-spin text-gray-400" />
            </div>
          ) : filteredAndSortedDocuments.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <User className="h-12 w-12 mx-auto mb-2 text-gray-300" />
              <p>Nessun ospite trovato</p>
              {activeFiltersCount > 0 && (
                <Button variant="link" onClick={clearFilters} className="mt-2">Rimuovi filtri</Button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-y">
                  <tr>
                    {[
                      { field: 'first_name', label: 'Nome' },
                      { field: 'last_name', label: 'Cognome' },
                      { field: 'guest_email', label: 'Email' },
                      { field: 'guest_phone', label: 'Telefono' },
                      { field: 'citizenship', label: 'Cittadinanza' },
                      { field: 'birth_date', label: 'Data Nascita' },
                      { field: 'document_number', label: 'Documento' },
                      { field: 'property_name', label: 'Immobile' },
                      { field: 'property_city', label: 'Comune' },
                      { field: 'property_province', label: 'Prov.' },
                      { field: 'check_in_date', label: 'Check-in' },
                      { field: 'check_out_date', label: 'Check-out' },
                      { field: 'created_at', label: 'Inserito' },
                    ].map(col => (
                      <th key={col.field}
                        className="px-3 py-3 text-left font-medium text-gray-600 cursor-pointer hover:bg-gray-100 whitespace-nowrap"
                        onClick={() => handleSort(col.field)}>
                        <div className="flex items-center gap-1">
                          {col.label}
                          <SortIcon field={col.field} />
                        </div>
                      </th>
                    ))}
                    <th className="px-3 py-3 text-center font-medium text-gray-600">Dettagli</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {filteredAndSortedDocuments.map((doc, i) => (
                    <tr key={doc.id || i} className="hover:bg-gray-50">
                      <td className="px-3 py-2.5 font-medium">{doc.first_name}</td>
                      <td className="px-3 py-2.5 font-medium">{doc.last_name}</td>
                      <td className="px-3 py-2.5">
                        {doc.guest_email ? (
                          <a href={`mailto:${doc.guest_email}`} className="text-blue-600 hover:underline text-xs truncate max-w-[160px] block">
                            {doc.guest_email}
                          </a>
                        ) : <span className="text-gray-300">-</span>}
                      </td>
                      <td className="px-3 py-2.5 whitespace-nowrap text-xs">
                        {doc.guest_phone ? (
                          <a href={`tel:${doc.guest_phone}`} className="text-blue-600 hover:underline">{doc.guest_phone}</a>
                        ) : <span className="text-gray-300">-</span>}
                      </td>
                      <td className="px-3 py-2.5 text-xs">{doc.citizenship || '-'}</td>
                      <td className="px-3 py-2.5 whitespace-nowrap text-xs">{doc.birth_date || '-'}</td>
                      <td className="px-3 py-2.5">
                        <div className="text-xs">
                          <span className="text-gray-500">{doc.document_type}:</span><br/>
                          <span className="font-mono">{doc.document_number}</span>
                        </div>
                      </td>
                      <td className="px-3 py-2.5">
                        <span className="truncate max-w-[120px] block text-xs notranslate" translate="no">{doc.property_name || '-'}</span>
                      </td>
                      <td className="px-3 py-2.5 text-xs">{doc.property_city || '-'}</td>
                      <td className="px-3 py-2.5 text-xs text-center">{doc.property_province || '-'}</td>
                      <td className="px-3 py-2.5 whitespace-nowrap text-xs">{doc.check_in_date || '-'}</td>
                      <td className="px-3 py-2.5 whitespace-nowrap text-xs">{doc.check_out_date || '-'}</td>
                      <td className="px-3 py-2.5 text-xs text-gray-500 whitespace-nowrap">
                        {doc.created_at ? new Date(doc.created_at).toLocaleDateString('it-IT') : '-'}
                      </td>
                      <td className="px-3 py-2.5 text-center">
                        <Button variant="ghost" size="sm" onClick={() => setSelectedDocument(doc)} data-testid={`view-btn-${i}`}>
                          <Eye className="h-4 w-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
      
      {selectedDocument && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" onClick={() => setSelectedDocument(null)}>
          <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <CardHeader className="flex flex-row items-center justify-between sticky top-0 bg-white z-10 border-b">
              <CardTitle className="text-lg">{selectedDocument.first_name} {selectedDocument.last_name}</CardTitle>
              <Button variant="ghost" size="sm" onClick={() => setSelectedDocument(null)}><X className="h-5 w-5" /></Button>
            </CardHeader>
            <CardContent className="p-6 space-y-6">
              <DetailSection icon={<User className="h-4 w-4" />} title="Dati Anagrafici" fields={[
                ['Nome', selectedDocument.first_name], ['Cognome', selectedDocument.last_name],
                ['Sesso', selectedDocument.gender === 'male' ? 'Maschio' : selectedDocument.gender === 'female' ? 'Femmina' : '-'],
                ['Tipo Ospite', selectedDocument.guest_type],
                ['Data Nascita', selectedDocument.birth_date], ['Comune Nascita', selectedDocument.birth_place],
                ['Provincia Nascita', selectedDocument.birth_province], ['Stato Nascita', selectedDocument.birth_country],
                ['Cittadinanza', selectedDocument.citizenship],
              ]} />
              {selectedDocument.codice_fiscale && (
                <div className="text-sm"><span className="text-gray-500">Codice Fiscale:</span>{' '}
                  <strong className="font-mono bg-gray-100 px-2 py-1 rounded">{selectedDocument.codice_fiscale}</strong>
                </div>
              )}
              <DetailSection icon={<Mail className="h-4 w-4" />} title="Contatti" fields={[
                ['Email', selectedDocument.guest_email], ['Telefono', selectedDocument.guest_phone],
              ]} />
              <DetailSection icon={<MapPin className="h-4 w-4" />} title="Residenza" fields={[
                ['Indirizzo', selectedDocument.residence_address, true],
                ['Comune', selectedDocument.residence_city], ['Provincia', selectedDocument.residence_province],
                ['CAP', selectedDocument.residence_postal_code], ['Stato', selectedDocument.residence_country],
              ]} />
              <DetailSection icon={<FileText className="h-4 w-4" />} title="Documento" fields={[
                ['Tipo', selectedDocument.document_type], ['Numero', selectedDocument.document_number],
                ['Rilasciato da', selectedDocument.document_issuer], ['Luogo Rilascio', selectedDocument.document_issue_place],
                ['Stato Rilascio', selectedDocument.document_issue_country],
                ['Data Rilascio', selectedDocument.document_issue_date], ['Scadenza', selectedDocument.document_expiry_date],
              ]} />
              <DetailSection icon={<Home className="h-4 w-4" />} title="Soggiorno" fields={[
                ['Immobile', selectedDocument.property_name], ['Comune', selectedDocument.property_city],
                ['Provincia', selectedDocument.property_province], ['Regione', selectedDocument.property_region],
                ['Check-in', selectedDocument.check_in_date], ['Check-out', selectedDocument.check_out_date],
              ]} />
              <div className="text-xs text-gray-400 pt-4 border-t">
                <p>Inserito il: {selectedDocument.created_at ? new Date(selectedDocument.created_at).toLocaleString('it-IT') : '-'}</p>
                <p>Booking ID: {selectedDocument.booking_id}</p>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
      </div>
    </Layout>
  );
}

function FilterSelect({ label, value, onChange, options, testId }) {
  return (
    <div>
      <label className="text-xs text-gray-500 mb-1 block">{label}</label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="h-9" data-testid={testId}>
          <SelectValue placeholder="Tutti" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">Tutti</SelectItem>
          {options.map(opt => (
            <SelectItem key={opt} value={opt}>{opt}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function DetailSection({ icon, title, fields }) {
  return (
    <div>
      <h4 className="text-sm font-semibold text-gray-500 uppercase mb-3 flex items-center gap-2">
        {icon} {title}
      </h4>
      <div className="grid grid-cols-2 gap-3 text-sm">
        {fields.map(([label, value, fullWidth], i) => (
          <div key={i} className={fullWidth ? 'col-span-2' : ''}>
            <span className="text-gray-500">{label}:</span>{' '}
            <strong>{value || '-'}</strong>
          </div>
        ))}
      </div>
    </div>
  );
}
